export type RocketReaderPlanetName =
  | 'Moon Meadow'
  | 'Candy Mars'
  | 'Rainbow Saturn'
  | 'Story Jupiter'
  | 'Galaxy Garden';

export type RocketReaderPlanet = {
  name: RocketReaderPlanetName;
  unlockText: string;
  color: string;
  accent: string;
};

export type RocketReaderChallenge = {
  id: number;
  level: 1 | 2;
  planet: RocketReaderPlanetName;
  readingText: string;
  question: string;
  options: string[];
  correctAnswer: string;
  rewardFuel: number;
  rewardStars: number;
};

export const ROCKET_READER_PLANETS: RocketReaderPlanet[] = [
  {
    name: 'Moon Meadow',
    unlockText: 'Soft moon grass and silver clouds are ready.',
    color: '#dce8ff',
    accent: '#7dd3fc',
  },
  {
    name: 'Candy Mars',
    unlockText: 'Sweet red hills glow beside the rocket trail.',
    color: '#ff8a7a',
    accent: '#ffd166',
  },
  {
    name: 'Rainbow Saturn',
    unlockText: 'Bright rings spin with every good answer.',
    color: '#b78cff',
    accent: '#7cf5c7',
  },
  {
    name: 'Story Jupiter',
    unlockText: 'Big story storms swirl with friendly clues.',
    color: '#ffcf70',
    accent: '#ff8a4c',
  },
  {
    name: 'Galaxy Garden',
    unlockText: 'Star flowers bloom at the edge of space.',
    color: '#7cf5c7',
    accent: '#f7a8ff',
  },
];

export const rocketReaderChallenges: RocketReaderChallenge[] = [
  {
    id: 1,
    level: 1,
    planet: 'Moon Meadow',
    readingText: 'Luna the rabbit saw a shiny star near the rocket.',
    question: 'What did Luna see?',
    options: ['A shiny star', 'A red car', 'A big fish'],
    correctAnswer: 'A shiny star',
    rewardFuel: 25,
    rewardStars: 1,
  },
  {
    id: 2,
    level: 1,
    planet: 'Moon Meadow',
    readingText: 'Milo packed three blue books before the rocket beeped.',
    question: 'How many books did Milo pack?',
    options: ['One book', 'Three books', 'Ten books'],
    correctAnswer: 'Three books',
    rewardFuel: 25,
    rewardStars: 1,
  },
  {
    id: 3,
    level: 1,
    planet: 'Moon Meadow',
    readingText: 'The moon bell rang softly, and the team waved goodbye.',
    question: 'What rang softly?',
    options: ['The moon bell', 'The lunch box', 'The garden gate'],
    correctAnswer: 'The moon bell',
    rewardFuel: 25,
    rewardStars: 1,
  },
  {
    id: 4,
    level: 1,
    planet: 'Candy Mars',
    readingText: 'A pink rover rolled past a hill made of peppermint rocks.',
    question: 'What rolled past the hill?',
    options: ['A pink rover', 'A sleepy turtle', 'A yellow kite'],
    correctAnswer: 'A pink rover',
    rewardFuel: 25,
    rewardStars: 1,
  },
  {
    id: 5,
    level: 1,
    planet: 'Candy Mars',
    readingText: 'Nora shared one cookie with Sam and saved two for later.',
    question: 'What did Nora save for later?',
    options: ['Two cookies', 'A green hat', 'Five rockets'],
    correctAnswer: 'Two cookies',
    rewardFuel: 25,
    rewardStars: 1,
  },
  {
    id: 6,
    level: 2,
    planet: 'Rainbow Saturn',
    readingText:
      'The rocket flew through seven bright rings, and each ring hummed a gentle tune.',
    question: 'What hummed a gentle tune?',
    options: ['The bright rings', 'The sleeping moon', 'The picnic basket'],
    correctAnswer: 'The bright rings',
    rewardFuel: 25,
    rewardStars: 2,
  },
  {
    id: 7,
    level: 2,
    planet: 'Rainbow Saturn',
    readingText:
      'Zara found a rainbow map under her seat and used it to choose the safe path.',
    question: 'Why did Zara use the map?',
    options: ['To choose the safe path', 'To bake a cake', 'To hide a shoe'],
    correctAnswer: 'To choose the safe path',
    rewardFuel: 25,
    rewardStars: 2,
  },
  {
    id: 8,
    level: 2,
    planet: 'Story Jupiter',
    readingText:
      'Jupiter had a tiny library cloud where every book opened with a warm hello.',
    question: 'Where did the books say hello?',
    options: ['In a library cloud', 'Inside a pond', 'Under a red bridge'],
    correctAnswer: 'In a library cloud',
    rewardFuel: 25,
    rewardStars: 2,
  },
  {
    id: 9,
    level: 2,
    planet: 'Story Jupiter',
    readingText:
      'Omar read the clue twice, smiled, and pointed the rocket toward the golden storm.',
    question: 'What did Omar do before he smiled?',
    options: ['Read the clue twice', 'Dropped his lunch', 'Closed the window'],
    correctAnswer: 'Read the clue twice',
    rewardFuel: 25,
    rewardStars: 2,
  },
  {
    id: 10,
    level: 2,
    planet: 'Story Jupiter',
    readingText:
      'The captain whispered, "Every careful reader can find the next star road."',
    question: 'Who can find the next star road?',
    options: ['Every careful reader', 'Only the loud robot', 'A lost sandwich'],
    correctAnswer: 'Every careful reader',
    rewardFuel: 25,
    rewardStars: 2,
  },
  {
    id: 11,
    level: 2,
    planet: 'Galaxy Garden',
    readingText:
      'In Galaxy Garden, tiny flowers opened whenever the crew spoke kind words.',
    question: 'When did the flowers open?',
    options: ['When the crew spoke kind words', 'When rain turned purple', 'When the rocket slept'],
    correctAnswer: 'When the crew spoke kind words',
    rewardFuel: 25,
    rewardStars: 2,
  },
  {
    id: 12,
    level: 2,
    planet: 'Galaxy Garden',
    readingText:
      'At the last gate, Lina read the sign slowly and found the path home.',
    question: 'How did Lina read the sign?',
    options: ['Slowly', 'Angrily', 'Upside down'],
    correctAnswer: 'Slowly',
    rewardFuel: 25,
    rewardStars: 2,
  },
];

export function firstChallengeIndexForPlanet(planet: RocketReaderPlanetName) {
  const index = rocketReaderChallenges.findIndex((challenge) => challenge.planet === planet);
  return index === -1 ? 0 : index;
}

export function getNextChallengeIndexForPlanet(
  currentIndex: number,
  planet: RocketReaderPlanetName,
) {
  const planetIndexes = rocketReaderChallenges
    .map((challenge, index) => ({ challenge, index }))
    .filter(({ challenge }) => challenge.planet === planet)
    .map(({ index }) => index);

  if (planetIndexes.length === 0) return 0;

  const position = planetIndexes.indexOf(currentIndex);
  if (position === -1) return planetIndexes[0];

  return planetIndexes[(position + 1) % planetIndexes.length];
}
